import React, { useRef, useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import { ArrowIcon } from './Svgs';
import styles from './ScrollCueBox.module.scss';
import BG_IMAGE_URL from '../../public/static/images/InfiniteScroll/paralaxBG.png';
import { useBreakpoint } from '@buzzfeed/react-components';

const MIN_HEIGHT = 120;
const MAX_HEIGHT = 200;
const PARALLAX_ZONE = 1200;
const OFFSCREEN_THRESHOLD = 160;
const TEXT_TOP_MIN = 40;
const TEXT_TOP_MAX = 60;
const BASE_ARROW_SPACING = 20;
const SCROLL_STEP = 15;
const SHRINK_VISIBILITY_THRESHOLD = 40;
const SCROLL_SENSITIVITY_UP = 1;

export const ReturnButton = ({ previousPath }) => {
  const [hover, setHover] = useState(false);

  const handleClick = () => {
    if (previousPath) window.location.href = previousPath;
  };

  return (
    <button
      className={styles.returnButton}
      type="button"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={handleClick}
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      <span
        className={styles.returnButton__icon}
        style={{
          marginRight: '12px',
          display: 'inline-flex',
          alignItems: 'center',
          transform: hover ? 'rotate(90deg)' : 'rotate(180deg)',
          transition: 'transform 0.2s ease',
        }}
      >
        <ArrowIcon width={16} height={16} />
      </span>
      <span className={styles.returnButton__text}>Return to Previous Article</span>
    </button>
  );
};

export const ScrollCueFinalState = ({ previousPath }) => (
  <div style={{ position: 'relative', height: `${MIN_HEIGHT}px` }}>
    <div
      className={clsx(styles.scrollCueBox, styles.isReturning)}
      style={{
        position: 'relative',
        height: `${MIN_HEIGHT}px`,
        backgroundImage: `url(${BG_IMAGE_URL})`,
      }}
    >
      <div className={styles.overlayHolder}>
        <ReturnButton previousPath={previousPath} />
      </div>
    </div>
  </div>
);

const ScrollCueBox = ({
  previousPath = null,
  onExpandComplete = () => {},
  onOffscreenScrollDown = () => {},
}) => {
  const isMobile = useBreakpoint('(max-width: 600px)');
  const SCROLL_SENSITIVITY_DOWN = isMobile ? 1 : 3;

  const isSafari = typeof window !== 'undefined' &&
    /Safari/.test(navigator.userAgent) &&
    !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR/.test(navigator.userAgent);

  const ref = useRef(null);
  const containerRef = useRef(null);
  const hasExpanded = useRef(false);
  const autoFrame = useRef(null);
  const throttleRef = useRef(false);

  const [arrowCount, setArrowCount] = useState(3);
  const [hideCue, setHideCue] = useState(false);
  const [hideContent, setHideContent] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [currentHeight, setCurrentHeight] = useState(MIN_HEIGHT);
  const [isVisible, setIsVisible] = useState(false);

  const arrowSizes = [
    { width: 36, height: 62 },
    { width: 32, height: 54 },
    { width: 24, height: 42 },
  ];

  const updateVisibility = () => {
    const rect = ref.current?.getBoundingClientRect();
    setIsVisible(rect && rect.top >= 0 && rect.bottom <= window.innerHeight);

    if (rect && rect.top <= SHRINK_VISIBILITY_THRESHOLD && !hideContent) {
      setCurrentHeight(MIN_HEIGHT);
      setArrowCount(3);
    }
  };

  const updateHeightFromScroll = (deltaY) => {
    const isScrollingDown = deltaY > 0;
    if (hideContent || (hasExpanded.current && isScrollingDown)) return;

    const sensitivity = isScrollingDown ? SCROLL_SENSITIVITY_DOWN : SCROLL_SENSITIVITY_UP;
    const amount = deltaY / sensitivity;

    setCurrentHeight((prev) => {
      let next = isScrollingDown
        ? Math.min(MAX_HEIGHT, prev + Math.abs(amount))
        : Math.max(MIN_HEIGHT, prev - Math.abs(amount));

      setArrowCount(next < 140 ? 1 : next < 180 ? 2 : 3);

      if (!hasExpanded.current && next >= MAX_HEIGHT - 10) {
        hasExpanded.current = true;
        setHideCue(true);
        onExpandComplete();
        requestAnimationFrame(stepScroll);
      }

      return next;
    });
  };

  const stepScroll = () => {
    const el = ref.current;
    if (!el || hideContent) return;

    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const gap = vh - rect.bottom;
    const p = Math.min(1, gap / PARALLAX_ZONE);

    el.style.backgroundPosition = `center ${100 - p * 40}%`;
    el.style.backgroundImage = `url(${BG_IMAGE_URL})`;

    if (rect.bottom > -OFFSCREEN_THRESHOLD) {
      if (!isSafari) {
        window.scrollBy(0, SCROLL_STEP);
      }
      autoFrame.current = requestAnimationFrame(stepScroll);
    } else {
      cancelAnimationFrame(autoFrame.current);
      el.style.backgroundPosition = 'center top';
      el.style.height = `${MIN_HEIGHT}px`;

      setTimeout(() => {
        setArrowCount(3);
        setCurrentHeight(MIN_HEIGHT);
        setHideContent(true);
        setIsReturning(true);
        onOffscreenScrollDown();
      }, 50);
    }
  };

  useEffect(() => {
    let lastTouchY = null;

    const throttledUpdate = (deltaY) => {
      if (throttleRef.current) return;
      throttleRef.current = true;
      updateHeightFromScroll(deltaY);
      setTimeout(() => {
        throttleRef.current = false;
      }, 16);
    };

    const handleWheel = (e) => {
      updateVisibility();
      if (isVisible) {
        throttledUpdate(e.deltaY);
      }
    };

    const handleTouchMove = (e) => {
      if (!isVisible || e.touches.length !== 1) return;
      const touchY = e.touches[0].clientY;
      if (lastTouchY !== null) {
        const deltaY = lastTouchY - touchY;
        throttledUpdate(deltaY);
      }
      lastTouchY = touchY;
    };

    const resetTouch = () => {
      lastTouchY = null;
    };

    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', resetTouch);
    window.addEventListener('touchcancel', resetTouch);
    window.addEventListener('scroll', updateVisibility, { passive: true });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', resetTouch);
      window.removeEventListener('touchcancel', resetTouch);
      window.removeEventListener('scroll', updateVisibility);
    };
  }, [isVisible, hideContent]);

  useEffect(() => {
    if (hideContent) {
      setCurrentHeight(MIN_HEIGHT);
      setArrowCount(3);
      setHideCue(false);
      hasExpanded.current = false;
    }
  }, [hideContent]);

  const progress = (currentHeight - MIN_HEIGHT) / (MAX_HEIGHT - MIN_HEIGHT);
  const textTop = TEXT_TOP_MIN + (TEXT_TOP_MAX - TEXT_TOP_MIN) * progress;

  const boxStyle = useMemo(() => ({
    position: 'relative',
    height: `${currentHeight}px`,
    transition: 'none',
    willChange: 'height, background-position',
    backgroundImage:
      !hideCue && !hideContent
        ? `linear-gradient(rgba(140,162,233,0.9), rgba(140,162,233,0.9)), url(${BG_IMAGE_URL})`
        : `url(${BG_IMAGE_URL})`,
    backgroundPosition: 'center top',
    backgroundRepeat: 'no-repeat',
  }), [currentHeight, hideCue, hideContent]);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', height: `${hideContent ? MIN_HEIGHT : MAX_HEIGHT}px` }}
    >
      <div
        ref={ref}
        tabIndex={0}
        className={clsx(styles.scrollCueBox, isReturning && styles.isReturning)}
        style={boxStyle}
      >
        {!hideCue && !hideContent && (
          <>
            <div className={styles.cueText} style={{ position: 'absolute', top: `${textTop}px` }}>
              KEEP SCROLLING FOR NEXT ARTICLE
            </div>
            <div className={styles.cueArrows}>
              {arrowSizes.slice(0, arrowCount).map((size, i) => {
                const offset = !hasExpanded.current ? BASE_ARROW_SPACING * progress * i : 0;
                if (offset === 0 && i > 0) return null;

                return (
                  <div
                    key={i}
                    className={styles.cueArrowItem}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      top: `${textTop + 24 + offset}px`,
                      zIndex: arrowCount - i,
                    }}
                  >
                    <ArrowIcon width={size.width} height={size.height} />
                  </div>
                );
              })}
            </div>
          </>
        )}
        {hideContent && (
          <div className={styles.overlayHolder}>
            <ReturnButton previousPath={previousPath} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ScrollCueBox;
